import { useTranslation } from 'react-i18next';
import logo from '../assets/brand/logo.png';
import { glassSurface } from './wallet/glass';

/**
 * First-open splash. A liquid-glass hero: holographic mesh backdrop, the floating
 * glass logo, and a frosted status pill while the wallet is created in the
 * background. Pass `leaving` to play the fade-out before unmounting.
 */
export function SplashScreen({ leaving }: { leaving?: boolean }) {
  const { t } = useTranslation();

  return (
    <div
      className={`fixed inset-0 z-50 flex flex-col items-center justify-center overflow-hidden ${
        leaving ? 'splash-out' : ''
      }`}
      style={{ background: '#f3f4fb' }}
    >
      {/* Holographic mesh — brand pastels matching the logo. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(58% 48% at 18% 22%, rgba(214,196,255,0.62), transparent 70%),' +
            'radial-gradient(54% 44% at 84% 16%, rgba(255,206,236,0.55), transparent 70%),' +
            'radial-gradient(60% 54% at 82% 82%, rgba(186,243,235,0.62), transparent 72%),' +
            'radial-gradient(56% 50% at 14% 84%, rgba(190,224,255,0.6), transparent 72%)',
        }}
      />
      {/* Drifting soft blobs for depth. */}
      <div
        aria-hidden
        className="splash-blob pointer-events-none absolute -left-16 top-20 h-64 w-64 rounded-full"
        style={{ background: 'radial-gradient(circle, rgba(255,200,234,0.55), transparent 70%)', filter: 'blur(22px)' }}
      />
      <div
        aria-hidden
        className="splash-blob pointer-events-none absolute -right-12 bottom-24 h-72 w-72 rounded-full"
        style={{
          background: 'radial-gradient(circle, rgba(186,236,255,0.5), transparent 70%)',
          filter: 'blur(22px)',
          animationDelay: '1.6s',
        }}
      />

      {/* Hero */}
      <div className="relative z-10 flex flex-col items-center">
        <img
          src={logo}
          alt={t('app.name')}
          className="splash-logo h-[150px] w-[150px] object-contain"
          style={{ filter: 'drop-shadow(0px 16px 46px rgba(125,140,255,0.32))' }}
        />
        <h1 className="mt-7 text-[27px] font-semibold tracking-tight text-ink">{t('app.name')}</h1>
        <p className="mt-1.5 text-[14px] text-muted">{t('splash.tagline')}</p>
      </div>

      {/* Status — frosted glass pill */}
      <div
        className="absolute bottom-16 z-10 flex items-center gap-3 rounded-full px-5 py-3"
        style={glassSurface('regular')}
      >
        <span className="flex items-center gap-1">
          <span className="splash-dot h-1.5 w-1.5 rounded-full bg-[#377dff]" />
          <span className="splash-dot h-1.5 w-1.5 rounded-full bg-[#377dff]" style={{ animationDelay: '0.15s' }} />
          <span className="splash-dot h-1.5 w-1.5 rounded-full bg-[#377dff]" style={{ animationDelay: '0.3s' }} />
        </span>
        <span className="text-[14px] font-medium text-ink">{t('splash.preparing')}</span>
      </div>
    </div>
  );
}
